import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Avatar,
  Fade,
  Zoom,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PaymentIcon from '@mui/icons-material/Payment';
import TapAndPlayIcon from '@mui/icons-material/TapAndPlay';
import SecurityIcon from '@mui/icons-material/Security';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import SpeedIcon from '@mui/icons-material/Speed';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import LoyaltyIcon from '@mui/icons-material/Loyalty';
import DeviceHubIcon from '@mui/icons-material/DeviceHub';

// Premium Color Palette
const colors = {
  navy: '#0a1128',
  darkNavy: '#060b1f',
  gold: '#c9a03d',
  lightGold: '#d4af37',
  darkGold: '#a0782c',
  charcoal: '#1a1a2e',
  lightCharcoal: '#16213e',
  pearl: '#f5f5f0',
  darkPearl: '#e8e8e0',
  white: '#ffffff',
  textDark: '#1a1a2e',
  textLight: '#6b6b6b',
  premiumDark: '#0d1117',
  premiumGold: '#b8942e',
};

const ProductCredit = () => {
  // Convenience Features - 4 cards (smaller)
  const convenienceFeatures = [
    { icon: <AccountBalanceIcon sx={{ fontSize: 22 }} />, title: "Online Banking", desc: "Real time balances, statements, manage orders" },
    { icon: <CreditCardIcon sx={{ fontSize: 22 }} />, title: "One Card", desc: "Consolidate all accounts into one method" },
    { icon: <PhoneAndroidIcon sx={{ fontSize: 22 }} />, title: "Mobile Pay", desc: "Link card with Mobile App to pay" },
    { icon: <TapAndPlayIcon sx={{ fontSize: 22 }} />, title: "Tap and Go", desc: "Easy payments at participating retailers" },
  ];

  // Rewards & Security Features - 4 cards
  const rewardsSecurityFeatures = [
    { icon: <LoyaltyIcon sx={{ fontSize: 24 }} />, title: "Cash Back Rewards", desc: "1.5% cash back on all your purchases with Diamond Rewards" },
    { icon: <DeviceHubIcon sx={{ fontSize: 24 }} />, title: "Secure Network", desc: "Absolute security every time you use your card" },
    { icon: <SecurityIcon sx={{ fontSize: 24 }} />, title: "Purchase Protection", desc: "90 day insurance on theft and accidental damage" },
    { icon: <PhoneAndroidIcon sx={{ fontSize: 24 }} />, title: "Mobile App", desc: "Check balances, view transactions, request payments" },
  ];

  const steps = ['Complete Application', 'No Credit Check', 'Instant Approval', 'Express Delivery'];

  const premiumFeatures = [
    { title: "1.5% Cash Back", description: "Earn rewards on every purchase" },
    { title: "Global Acceptance", description: "Accepted at millions of locations" },
    { title: "24/7 Support", description: "Round-the-clock concierge service" },
    { title: "Secure Network", description: "Absolute security every time" },
  ];

  return (
    <Box>
      {/* Hero Section - Reduced Overlay Opacity */}
      <Box 
        sx={{ 
          position: 'relative',
          py: { xs: 8, md: 10 }, 
                 minHeight: { xs: 450, md: 700},
          color: 'white', 
          textAlign: 'center',
          backgroundImage: 'url("/images/productspageherobg.jpg")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(13,17,23,0.4) 0%, rgba(26,26,46,0.3) 100%)',
          }
        }}
      >
        <Container sx={{ position: 'relative', zIndex: 1 }}>
          <Fade in timeout={1000}>
            <Box>
              <Avatar 
                src="/logo/logo.png" 
                alt="Diamond Card"
                sx={{ 
                  width: 80, 
                  height: 80, 
                  mx: 'auto', 
                  mb: 2,
                  backgroundColor: 'transparent',
                  '& img': { objectFit: 'contain' }
                }}
                variant="square"
              />
              <Typography 
                variant="h1" 
                sx={{ 
                  fontSize: { xs: '2rem', md: '3rem' }, 
                  mb: 1, 
                  fontWeight: 800,
                  background: `linear-gradient(135deg, ${colors.premiumGold} 0%, ${colors.lightGold} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                  letterSpacing: -0.5,
                }}
              >
                DIAMOND CARD
              </Typography>
              <Typography 
                variant="h2" 
                sx={{ 
                  fontSize: { xs: '1.8rem', md: '2.5rem' }, 
                  color: colors.premiumGold, 
                  mb: 1, 
                  fontWeight: 700,
                  letterSpacing: 2,
                }}
              >
                Credit Cards
              </Typography>
              <Typography variant="h6" sx={{ mt: 1, color: colors.premiumGold, fontWeight: 500, letterSpacing: 1 }}>
                Rewards everywhere — Premium lifestyle
              </Typography>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Premium Features Strip */}
      <Box sx={{ bgcolor: colors.premiumGold, py: 1.5 }}>
        <Container maxWidth="lg">
          <Grid container spacing={2} justifyContent="center">
            {premiumFeatures.map((feature, idx) => (
              <Grid item xs={6} sm={3} md={3} key={idx}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                  <CheckCircleIcon sx={{ color: colors.navy, fontSize: 16 }} />
                  <Typography variant="caption" sx={{ fontWeight: 600, color: colors.navy }}>
                    {feature.title}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Convenience Features Section - 4 cards in one row */}
      <Container sx={{ py: 6 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Chip 
            label="CONVENIENCE" 
            sx={{ 
              bgcolor: colors.premiumGold, 
              color: colors.navy, 
              fontWeight: 700,
              px: 2,
              py: 1,
              fontSize: '0.75rem',
              letterSpacing: 1,
              mb: 2,
            }}
          />
          <Typography 
            variant="h5" 
            textAlign="center" 
            sx={{ 
              fontWeight: 700, 
              color: colors.navy, 
              fontSize: { xs: '1.3rem', md: '1.6rem' }
            }}
          >
            Ultimate Convenience
          </Typography>
        </Box>
        
        <Grid container spacing={2.5} alignItems="stretch">
          {convenienceFeatures.map((feature, idx) => (
            <Zoom in timeout={300 + idx * 100} key={idx}>
              <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
                <Card 
                  sx={{ 
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 3,
                    transition: 'all 0.3s ease',
                    borderTop: `2px solid ${colors.premiumGold}`,
                    '&:hover': { 
                      transform: 'translateY(-4px)', 
                      boxShadow: '0 12px 24px rgba(0,0,0,0.1)',
                      '& .feature-icon': {
                        transform: 'scale(1.05)',
                        backgroundColor: colors.premiumGold,
                      }
                    } 
                  }}
                >
                  <CardContent sx={{ p: 2.5, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Avatar 
                      className="feature-icon"
                      sx={{ 
                        bgcolor: `${colors.premiumGold}1A`, 
                        color: colors.navy,
                        width: 50, 
                        height: 50, 
                        mb: 1.5,
                        transition: 'all 0.3s ease',
                      }}
                    >
                      {feature.icon}
                    </Avatar>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: colors.navy, mb: 1, fontSize: '0.95rem' }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5, fontSize: '0.8rem' }}>
                      {feature.desc}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Zoom>
          ))}
        </Grid>
      </Container>

      {/* Rewards & Security Section - 4 cards in one row with Background Image */}
      <Box 
        sx={{ 
          position: 'relative',
          py: 6,
          backgroundImage: 'url("/images/diamondcards.jpg")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `linear-gradient(135deg, ${colors.navy}DD 0%, ${colors.charcoal}CC 100%)`,
          }
        }}
      >
        <Container sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Chip 
              label="REWARDS & SECURITY" 
              sx={{ 
                bgcolor: colors.premiumGold, 
                color: colors.navy, 
                fontWeight: 700,
                px: 2,
                py: 1,
                fontSize: '0.75rem',
                letterSpacing: 1,
                mb: 2,
              }}
            />
            <Typography 
              variant="h4" 
              textAlign="center" 
              sx={{ 
                fontWeight: 700, 
                color: colors.white, 
                fontSize: { xs: '1.5rem', md: '1.8rem' }
              }}
            >
              Rewards & Security
            </Typography>
            <Typography 
              variant="body2" 
              textAlign="center" 
              sx={{ mt: 1, color: colors.white, opacity: 0.8 }}
            >
              Earn rewards while enjoying bank-grade security on every transaction
            </Typography>
          </Box>
          
          <Grid container spacing={2.5} alignItems="stretch">
            {rewardsSecurityFeatures.map((feature, idx) => (
              <Zoom in timeout={300 + idx * 100} key={idx}>
                <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
                  <Card 
                    sx={{ 
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                      borderTop: `2px solid ${colors.premiumGold}`,
                      bgcolor: 'rgba(26, 26, 46, 0.85)',
                      backdropFilter: 'blur(8px)',
                      '&:hover': { 
                        transform: 'translateY(-4px)', 
                        boxShadow: `0 12px 24px ${colors.premiumGold}40`,
                        '& .feature-icon': {
                          transform: 'scale(1.05)',
                          backgroundColor: colors.premiumGold,
                        }
                      } 
                    }}
                  >
                    <CardContent sx={{ p: 2.5, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      <Avatar 
                        className="feature-icon"
                        sx={{ 
                          bgcolor: `${colors.premiumGold}33`, 
                          color: colors.navy,
                          width: 50, 
                          height: 50, 
                          mb: 1.5,
                          transition: 'all 0.3s ease',
                        }}
                      >
                        {feature.icon}
                      </Avatar>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: colors.white, mb: 1, fontSize: '0.95rem' }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: colors.white, opacity: 0.8, lineHeight: 1.5, fontSize: '0.8rem' }}>
                        {feature.desc}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Zoom>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Diamond Advantage Section */}
      <Container sx={{ py: 6 }}>
        <Fade in timeout={800}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: { xs: 3, md: 4 }, 
              textAlign: 'center', 
              borderRadius: 3,
              background: `linear-gradient(135deg, ${colors.navy} 0%, ${colors.charcoal} 100%)`,
            }}
          >
            <WorkspacePremiumIcon sx={{ fontSize: 50, color: colors.premiumGold, mb: 1.5 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, color: colors.premiumGold, mb: 1.5, fontSize: { xs: '1.5rem', md: '1.8rem' } }}>
              Diamond Advantage
            </Typography>
            <Typography variant="body2" paragraph sx={{ maxWidth: 650, mx: 'auto', mb: 2, color: colors.white, opacity: 0.8 }}>
              The Credit Card Network that ensures absolute security every time you use your card! 
              Plus, earn 1.5% cash back on all purchases with Diamond Rewards.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Chip icon={<StarIcon />} label="1.5% Cash Back" size="small" sx={{ bgcolor: colors.premiumGold, color: colors.navy, fontWeight: 600 }} />
              <Chip icon={<SpeedIcon />} label="Secure Network" size="small" sx={{ bgcolor: `${colors.premiumGold}1A`, color: colors.premiumGold, fontWeight: 600 }} />
              <Chip icon={<CheckCircleIcon />} label="90 Day Protection" size="small" sx={{ bgcolor: `${colors.premiumGold}1A`, color: colors.premiumGold, fontWeight: 600 }} />
            </Box>
          </Paper>
        </Fade>
      </Container>

      {/* Purchase Protection & Mobile App - 2 Columns in one row */}
      <Container sx={{ pb: 6 }}>
        <Grid container spacing={3} alignItems="stretch">
          <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
            <Zoom in timeout={500}>
              <Paper sx={{ 
                p: 3, 
                borderRadius: 3, 
                bgcolor: colors.premiumGold, 
                textAlign: 'center',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                transition: 'all 0.3s',
                '&:hover': { transform: 'translateY(-3px)', boxShadow: `0 12px 24px ${colors.premiumGold}40` }
              }}>
                <SecurityIcon sx={{ fontSize: 40, color: colors.navy, mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: colors.navy, mb: 0.5, fontSize: '1.1rem' }}>
                  Purchase Protection
                </Typography>
                <Typography variant="body2" sx={{ color: colors.navy, opacity: 0.8 }}>
                  90 day insurance on theft and accidental damage.
                </Typography>
              </Paper>
            </Zoom>
          </Grid>
          <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
            <Zoom in timeout={500}>
              <Paper sx={{ 
                p: 3, 
                borderRadius: 3, 
                bgcolor: colors.navy, 
                textAlign: 'center',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                transition: 'all 0.3s',
                '&:hover': { transform: 'translateY(-3px)', boxShadow: `0 12px 24px ${colors.premiumGold}40` }
              }}>
                <PhoneAndroidIcon sx={{ fontSize: 40, color: colors.premiumGold, mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: colors.premiumGold, mb: 0.5, fontSize: '1.1rem' }}>
                  Mobile App
                </Typography>
                <Typography variant="body2" sx={{ color: colors.white, opacity: 0.8 }}>
                  Check balances, view transactions, request payments.
                </Typography>
              </Paper>
            </Zoom>
          </Grid>
        </Grid>
      </Container>

      {/* Signing Up Section */}
      <Box sx={{ bgcolor: colors.pearl, py: 6 }}>
        <Container>
          <Typography variant="h4" textAlign="center" sx={{ fontWeight: 700, color: colors.navy, mb: 1.5, fontSize: { xs: '1.5rem', md: '1.8rem' } }}>
            Simple Application Process
          </Typography>
          <Typography variant="body2" textAlign="center" sx={{ mb: 4, color: colors.premiumGold, fontWeight: 500 }}>
            No credit check • No minimum balance • No activation fee
          </Typography>
          
          <Stepper 
            alternativeLabel 
            activeStep={3} 
            sx={{ mb: 5, '& .MuiStepLabel-label': { fontSize: '0.75rem' } }}
          >
            {steps.map((label, idx) => (
              <Step key={label}>
                <StepLabel 
                  StepIconProps={{
                    sx: {
                      '& .MuiStepIcon-root': {
                        fontSize: '1.5rem',
                        color: idx <= 3 ? colors.premiumGold : '#ccc',
                        '&.Mui-active': { color: colors.premiumGold },
                        '&.Mui-completed': { color: colors.premiumGold },
                      }
                    }
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          <Paper 
            sx={{ 
              p: { xs: 3, md: 4 }, 
              textAlign: 'center', 
              borderRadius: 3,
              background: `linear-gradient(135deg, ${colors.navy} 0%, ${colors.charcoal} 100%)`,
            }}
          >
            <Typography variant="h5" sx={{ color: colors.premiumGold, fontWeight: 700, mb: 1.5 }}>
              Applying takes just 10 minutes!
            </Typography>
            <Typography variant="body2" sx={{ color: colors.white, opacity: 0.8, mb: 2 }}>
              Join the exclusive Diamond Card community and experience banking redefined
            </Typography>
            <Button 
              variant="contained" 
              size="medium" 
              sx={{ 
                bgcolor: colors.premiumGold,
                color: colors.navy,
                fontWeight: 700, 
                px: 4, 
                py: 1.2,
                fontSize: '0.85rem',
                borderRadius: 50,
                '&:hover': { bgcolor: colors.lightGold, transform: 'translateY(-2px)' },
              }}
            >
              Order your Diamond Card now — receive it within 14 business days
            </Button>
          </Paper>
        </Container>
      </Box>

      {/* Trust Badges */}
      <Container sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="caption" sx={{ color: colors.textLight }}>
          Trusted by thousands of premium cardholders worldwide
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, flexWrap: 'wrap', mt: 1 }}>
          <Chip label="★★★★★ 4.9 Rating" size="small" sx={{ bgcolor: colors.pearl, color: colors.navy, fontWeight: 500 }} />
          <Chip label="PCI Compliant" size="small" sx={{ bgcolor: colors.pearl, color: colors.navy, fontWeight: 500 }} />
          <Chip label="FDIC Insured" size="small" sx={{ bgcolor: colors.pearl, color: colors.navy, fontWeight: 500 }} />
          <Chip label="24/7 Support" size="small" sx={{ bgcolor: colors.pearl, color: colors.navy, fontWeight: 500 }} />
        </Box>
      </Container>
    </Box>
  );
};

export default ProductCredit;